import { type Fandom } from "@prisma/client";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import SVGAtom from "~/components/Atoms/SVGAtom/SVGAtom";
import useInterval from "~/hooks/useInterval";

type StakeProps = {
  stakeAmount?: number;
  stakeAmountPrice?: number;
  totalRewardAmount?: number;
};

type UnstakeProps = {
  stakeAmount?: number;
  stakeAmountPrice?: number;
  claimTimestamp: number;
};

type Props = {
  fandom: Fandom;

  stakeProps?: StakeProps;
  unstakeProps?: UnstakeProps;
};

export const JoinedClubCard = (props: Props) => {
  const [unstakeTimestamp, setUnstakeTimestamp] = useState(0);
  const [timeUntilUnstaked, setTimeUntilUnstaked] =
    useState<string>("336:00:00");
  const [claimAvailable, setClaimAvailable] = useState<boolean>(false);

  const updateUntilUnstaked = (timestamp: number) => {
    if (timestamp <= 0) return;

    const unstakeDelta = new Date(timestamp - Date.now()).getTime();
    // 0초가 되어서 unstake 가능해질 때.
    if (unstakeDelta <= 0) {
      setClaimAvailable(true);
      return;
    }

    const pad = (num: number) => {
      if (num < 10) return `0${num}`;
      return `${num}`;
    };

    const unstakeSeconds = unstakeDelta / 1000;

    const hours = Math.floor(unstakeSeconds / 60 / 60);
    const minutes = Math.floor(unstakeSeconds / 60) % 60;
    const seconds = Math.floor(unstakeSeconds) % 60;

    setTimeUntilUnstaked(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
  };

  useInterval(() => {
    updateUntilUnstaked(unstakeTimestamp);
  }, 1000);

  useEffect(() => {
    if (props.unstakeProps) {
      const unstakeTimestamp = new Date(
        props.unstakeProps.claimTimestamp,
      ).getTime();
      setUnstakeTimestamp(unstakeTimestamp);
    }
  }, [props.unstakeProps]);

  return (
    <Link
      href={`/fandom/${props.fandom.id}`}
      className="flex h-fit w-full items-center justify-between"
    >
      <div
        className="flex flex-grow items-center justify-start gap-[14px] pr-[14px]"
        style={{ maxWidth: "calc(100% - 16px)" }}
      >
        <div className="relative h-[50px] w-[50px] overflow-hidden rounded-full">
          <Image
            src={props.fandom.imageUrl}
            alt="fandom image"
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div
          className="flex max-w-full flex-grow flex-col items-start"
          style={{ maxWidth: "calc(100% - 64px)" }}
        >
          <div className="flex w-fit max-w-full gap-[3px] text-[13px] font-bold leading-[20px]">
            <p className="flex-grow truncate break-words">
              {props.fandom.title}
            </p>
            <p className="w-fit">Club</p>
          </div>
          {props.stakeProps && (
            <p className="w-full whitespace-pre-wrap break-words text-[13px] leading-[20px]">
              {`Deposited ${
                props.stakeProps.stakeAmount?.toLocaleString() ?? 0
              } FAND (=$${
                props.stakeProps.stakeAmountPrice?.toLocaleString() ?? 0
              })\nEarned ${
                props.stakeProps.totalRewardAmount?.toLocaleString() ?? 0
              } FAO`}
            </p>
          )}
          {props.unstakeProps && (
            <>
              <p className="w-full whitespace-pre-wrap break-words text-[13px] font-bold leading-[20px]">
                {!claimAvailable
                  ? `Leaving... ${timeUntilUnstaked}`
                  : "Leave Complete 00:00:00"}
              </p>
              <p className="w-full whitespace-pre-wrap break-words text-[13px] leading-[20px]">
                {`Refund ${
                  props.unstakeProps.stakeAmount?.toLocaleString() ?? 0
                } FAND (=$${
                  props.unstakeProps.stakeAmountPrice?.toLocaleString() ?? 0
                })`}
              </p>
            </>
          )}
        </div>
      </div>
      <div className="h-[16px] w-[16px]">
        <SVGAtom iconName="arrowRight" width={16} height={16} color="#000" />
      </div>
    </Link>
  );
};
